import { useEffect, useRef, useState, useCallback } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { getAnalyser, getContext, createSmoothTone, stopAll, playHyperbeam, playTone, unlockAudio, isAudioUnlocked } from "@/lib/audioEngine";
import { Radio, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FORMATIONS = ["96", "tetragrammaton", "merkabah", "13", "SOUNDdD", "sphere", "spiral", "wave", "dna", "vortex", "grid", "EB", "HEAL"];
const PARTICLE_COUNT = 1396;

const EB_AUDIO_URL = "https://customer-assets.emergentagent.com/job_3a6acb3e-074e-41f3-a0be-676978ae6151/artifacts/ltsnqanm_fibrous_dysplasia_comfort_harmonic_stack_33sec.wav";

const CHAKRAS = [
  { name: "ROOT",       hz: 396, color: "#FF0000", hue: 0   },
  { name: "SACRAL",     hz: 417, color: "#FF6600", hue: 25  },
  { name: "SOLAR",      hz: 528, color: "#FFCC00", hue: 48  },
  { name: "HEART",      hz: 639, color: "#33CC33", hue: 120 },
  { name: "THROAT",     hz: 741, color: "#3399FF", hue: 210 },
  { name: "THIRD EYE",  hz: 852, color: "#6633CC", hue: 265 },
  { name: "CROWN",      hz: 963, color: "#CC33FF", hue: 290 },
];

function chakraColorAt(ratio) {
  const idx = Math.min(6, Math.floor(ratio * 7));
  const next = Math.min(6, idx + 1);
  const localT = (ratio * 7) - idx;
  return CHAKRAS[idx].hue + (CHAKRAS[next].hue - CHAKRAS[idx].hue) * localT;
}
function freqAtRatio(r) { return 396 + r * (963 - 396); }
function chakraIndexAtFreq(hz) {
  let c = 0, d = Infinity;
  for (let i = 0; i < 7; i++) { const dd = Math.abs(CHAKRAS[i].hz - hz); if (dd < d) { d = dd; c = i; } }
  return c;
}

/* "96" shape — render text to offscreen canvas, sample pixel positions */
const textPointsCache = { points: null };
function get96Points(count) {
  if (textPointsCache.points && textPointsCache.points.length >= count) return textPointsCache.points;
  const off = document.createElement("canvas");
  const s = 200;
  off.width = s; off.height = s;
  const c = off.getContext("2d");
  c.fillStyle = "#000"; c.fillRect(0, 0, s, s);
  c.font = "bold 120px monospace";
  c.textAlign = "center"; c.textBaseline = "middle";
  c.fillStyle = "#fff";
  c.fillText("96", s / 2, s / 2);
  const img = c.getImageData(0, 0, s, s).data;
  // Collect all white pixel positions
  const filled = [];
  for (let y = 0; y < s; y += 2) {
    for (let x = 0; x < s; x += 2) {
      if (img[(y * s + x) * 4] > 128) {
        filled.push({ x: (x / s - 0.5) * 0.8, y: (y / s - 0.5) * 0.65 });
      }
    }
  }
  // Sample `count` points evenly from filled pixels
  const pts = [];
  if (filled.length === 0) {
    for (let i = 0; i < count; i++) pts.push({ x: (Math.random() - 0.5) * 0.5, y: (Math.random() - 0.5) * 0.5 });
  } else {
    for (let i = 0; i < count; i++) {
      pts.push(filled[Math.floor((i / count) * filled.length)]);
    }
  }
  textPointsCache.points = pts;
  return pts;
}

/* "13" shape — pixel sampled like "96" */
const text13Cache = { points: null };
function get13Points(count) {
  if (text13Cache.points && text13Cache.points.length >= count) return text13Cache.points;
  const off = document.createElement("canvas");
  const s = 200;
  off.width = s; off.height = s;
  const c = off.getContext("2d");
  c.fillStyle = "#000"; c.fillRect(0, 0, s, s);
  c.font = "bold 140px monospace";
  c.textAlign = "center"; c.textBaseline = "middle";
  c.fillStyle = "#fff";
  c.fillText("13", s / 2, s / 2);
  const img = c.getImageData(0, 0, s, s).data;
  const filled = [];
  for (let y = 0; y < s; y += 2) {
    for (let x = 0; x < s; x += 2) {
      if (img[(y * s + x) * 4] > 128) filled.push({ x: (x / s - 0.5) * 0.8, y: (y / s - 0.5) * 0.65 });
    }
  }
  const pts = [];
  if (filled.length === 0) { for (let i = 0; i < count; i++) pts.push({ x: (Math.random() - 0.5) * 0.5, y: (Math.random() - 0.5) * 0.5 }); }
  else { for (let i = 0; i < count; i++) pts.push(filled[Math.floor((i / count) * filled.length)]); }
  text13Cache.points = pts;
  return pts;
}

/* "SOUNDdD" shape — pixel sampled, wider canvas for longer text */
const textSOUNDdDCache = { points: null };
function getSOUNDdDPoints(count) {
  if (textSOUNDdDCache.points && textSOUNDdDCache.points.length >= count) return textSOUNDdDCache.points;
  const off = document.createElement("canvas");
  const w = 400, h = 160;
  off.width = w; off.height = h;
  const c = off.getContext("2d");
  c.fillStyle = "#000"; c.fillRect(0, 0, w, h);
  c.font = "bold 64px monospace";
  c.textAlign = "center"; c.textBaseline = "middle";
  c.fillStyle = "#fff";
  c.fillText("SOUNDdD", w / 2, h / 2);
  const img = c.getImageData(0, 0, w, h).data;
  const filled = [];
  for (let y = 0; y < h; y += 2) {
    for (let x = 0; x < w; x += 2) {
      if (img[(y * w + x) * 4] > 128) filled.push({ x: (x / w - 0.5) * 0.9, y: (y / h - 0.5) * 0.5 });
    }
  }
  const pts = [];
  if (filled.length === 0) { for (let i = 0; i < count; i++) pts.push({ x: (Math.random() - 0.5) * 0.5, y: (Math.random() - 0.5) * 0.3 }); }
  else { for (let i = 0; i < count; i++) pts.push(filled[Math.floor((i / count) * filled.length)]); }
  textSOUNDdDCache.points = pts;
  return pts;
}

/* "E.B." shape — Knicks-styled initials in T2 (Orbitron) font */
const textEBCache = { points: null, fontLoaded: false };
function getEBPoints(count) {
  // Invalidate cache if font just loaded
  const fontReady = document.fonts?.check?.("900 180px Orbitron") ?? false;
  if (textEBCache.points && textEBCache.points.length >= count && textEBCache.fontLoaded === fontReady) return textEBCache.points;
  const off = document.createElement("canvas");
  const w = 600, h = 300;
  off.width = w; off.height = h;
  const c = off.getContext("2d");
  c.fillStyle = "#000"; c.fillRect(0, 0, w, h);
  // T2 Terminator-style font — Orbitron Black 900 weight
  c.font = "900 180px 'Orbitron', sans-serif";
  c.textAlign = "center"; c.textBaseline = "middle";
  // Draw with slight stroke for extra boldness/clarity
  c.strokeStyle = "#fff";
  c.lineWidth = 3;
  c.strokeText("E.B.", w / 2, h / 2);
  c.fillStyle = "#fff";
  c.fillText("E.B.", w / 2, h / 2);
  const img = c.getImageData(0, 0, w, h).data;
  const filled = [];
  // Dense sampling (every pixel) for sharp edges
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (img[(y * w + x) * 4] > 100) filled.push({ x: (x / w - 0.5) * 0.9, y: (y / h - 0.5) * 0.5 });
    }
  }
  const pts = [];
  if (filled.length === 0) { for (let i = 0; i < count; i++) pts.push({ x: (Math.random() - 0.5) * 0.5, y: (Math.random() - 0.5) * 0.3 }); }
  else { for (let i = 0; i < count; i++) pts.push(filled[Math.floor((i / count) * filled.length)]); }
  textEBCache.points = pts;
  textEBCache.fontLoaded = fontReady;
  return pts;
}

/* Haptic feedback */
function haptic(ms) {
  try { if (navigator.vibrate) navigator.vibrate(ms); } catch {}
}

// Continuous haptic pattern scaled to frequency — lower freq = longer pulses, higher = shorter rapid pulses
function hapticForFreq(hz) {
  try {
    if (!navigator.vibrate) return;
    if (hz < 200) navigator.vibrate([30, 40]);
    else if (hz < 400) navigator.vibrate([20, 30]);
    else if (hz < 600) navigator.vibrate([15, 20]);
    else if (hz < 800) navigator.vibrate([10, 15]);
    else navigator.vibrate([8, 10, 8]);
  } catch {}
}

// Strong formation-select haptic — double pulse
function hapticFormation() {
  try { if (navigator.vibrate) navigator.vibrate([30, 50, 30]); } catch {}
}

function ParticleLab({ onBrowseArchive }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, active: false, down: false });
  const particlesRef = useRef([]);
  const [formation, setFormation] = useState("96");
  const [speed, setSpeed] = useState(1);
  const [chaos, setChaos] = useState(0);
  const [paused, setPaused] = useState(false);
  const timeRef = useRef(0);

  const [soundOn, setSoundOn] = useState(false);
  const [liveFreq, setLiveFreq] = useState(null);
  const [activeChakra, setActiveChakra] = useState(null);
  const [healMode, setHealMode] = useState(false);
  const healRef = useRef({ idx: 0, timer: 0 });

  // Applied frequency state
  const [appliedFreq, setAppliedFreq] = useState(null);
  const [presets, setPresets] = useState(null);
  const [showPresets, setShowPresets] = useState(false);
  const appliedNodeRef = useRef(null);

  // GLYPH secret menu state
  const [glyphOpen, setGlyphOpen] = useState(false);
  const [glyphPalette, setGlyphPalette] = useState(null);
  const [glyphMotion, setGlyphMotion] = useState(null);
  const [glyphCharset, setGlyphCharset] = useState(null);
  const glyphTapRef = useRef(0);

  // Preload Orbitron font for EB formation
  useEffect(() => {
    if (typeof document.fonts?.load === "function") {
      document.fonts.load("900 180px Orbitron").then(() => {
        textEBCache.points = null; // invalidate so next render uses the real font
      }).catch(() => {});
    }
  }, []);
  const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
  const hyperbeamRef = useRef(null);
  const ebAudioRef = useRef(null);
  const ebBgImgRef = useRef(null);

  // Preload EB background image
  useEffect(() => {
    const img = new Image();
    img.src = "/eb-bg.png";
    img.onload = () => { ebBgImgRef.current = img; };
  }, []);
  const audioDataRef = useRef({ bass: 0, mid: 0, high: 0, energy: 0, freqPeak: 0 });
  const toneRef = useRef(null);
  const lastFreqRef = useRef(0);
  const lastFrameRef = useRef(0);

  useEffect(() => {
    const p = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      p.push({ x: 0, y: 0, tx: 0, ty: 0, vx: 0, vy: 0, hue: 0, size: 1 });
    }
    particlesRef.current = p;
  }, []);

  // Audio analyser
  useEffect(() => {
    if (!soundOn) { audioDataRef.current = { bass: 0, mid: 0, high: 0, energy: 0, freqPeak: 0 }; return; }
    let running = true;
    const analyze = () => {
      if (!running) return;
      try {
        const analyser = getAnalyser();
        const data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(data);
        const len = data.length;
        let bass = 0, mid = 0, high = 0, peakIdx = 0, peakVal = 0;
        const bEnd = Math.floor(len * 0.12), mEnd = Math.floor(len * 0.45);
        for (let i = 0; i < len; i++) {
          if (i < bEnd) bass += data[i];
          else if (i < mEnd) mid += data[i];
          else high += data[i];
          if (data[i] > peakVal) { peakVal = data[i]; peakIdx = i; }
        }
        bass /= (bEnd * 255); mid /= ((mEnd - bEnd) * 255); high /= ((len - mEnd) * 255);
        audioDataRef.current = { bass, mid, high, energy: bass * 0.45 + mid * 0.35 + high * 0.2, freqPeak: peakIdx / len };
      } catch {}
      requestAnimationFrame(analyze);
    };
    analyze();
    return () => { running = false; };
  }, [soundOn]);

  // Heal mode — smooth glide through chakra frequencies (no oscillator recreation)
  useEffect(() => {
    if (!healMode || !soundOn) return;
    if (!toneRef.current || !toneRef.current.isAlive) {
      toneRef.current = createSmoothTone({ freq: CHAKRAS[0].hz, gain: 0.15 });
      toneRef.current.fadeIn(0.15);
    }
    let idx = 0;
    const cycle = () => {
      if (toneRef.current && toneRef.current.isAlive) {
        toneRef.current.setFrequency(CHAKRAS[idx].hz);
      }
      setLiveFreq(CHAKRAS[idx].hz);
      setActiveChakra(idx);
      hapticForFreq(CHAKRAS[idx].hz);
      idx = (idx + 1) % 7;
    };
    cycle();
    const iv = setInterval(cycle, 4000);
    return () => { clearInterval(iv); };
  }, [healMode, soundOn]);

  // Load presets on first mount
  useEffect(() => {
    fetch(`${API_BASE}/particle-lab/presets`)
      .then(r => r.json())
      .then(setPresets)
      .catch(() => {});
  }, [API_BASE]);

  // Apply a frequency from archive / presets
  const applyFrequency = useCallback(async (freq, name = "", source = "archive") => {
    // Stop any previous applied tone
    if (appliedNodeRef.current && appliedNodeRef.current.isAlive) {
      appliedNodeRef.current.fadeOut(0.05);
    }
    // Enable sound if not already on
    if (!soundOn) setSoundOn(true);

    // Create the tone
    const node = createSmoothTone({ freq, gain: 0.0 });
    node.fadeIn(0.2);
    appliedNodeRef.current = node;
    setAppliedFreq({ frequency: freq, name: name || `${freq} Hz` });
    setLiveFreq(freq);
    hapticFormation();
    toast.success(`Applied ${freq} Hz — ${name || "Custom"}`);

    // Log to backend
    try {
      await fetch(`${API_BASE}/particle-lab/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ frequency: freq, name, source, gain: 0.2 }),
      });
    } catch { /* non-critical */ }
  }, [soundOn, API_BASE]);

  const stopApplied = useCallback(() => {
    if (appliedNodeRef.current && appliedNodeRef.current.isAlive) {
      appliedNodeRef.current.fadeOut(0.1);
    }
    appliedNodeRef.current = null;
    setAppliedFreq(null);
  }, []);


  const updateMouseTone = useCallback((mx) => {
    if (!soundOn || !mouseRef.current.down || healMode) return;
    const hz = freqAtRatio(mx);
    const gain = 0.12 + (1 - mouseRef.current.y) * 0.18;

    if (!toneRef.current || !toneRef.current.isAlive) {
      toneRef.current = createSmoothTone({ freq: hz, gain });
      toneRef.current.fadeIn(gain);
    } else {
      toneRef.current.setFrequency(hz);
      toneRef.current.setGain(gain);
    }

    if (Math.abs(hz - lastFreqRef.current) > 3) {
      lastFreqRef.current = hz;
      setLiveFreq(Math.round(hz));
      setActiveChakra(chakraIndexAtFreq(hz));
      hapticForFreq(hz);
    }
  }, [soundOn, healMode]);

  const stopMouseTone = useCallback(() => {
    if (healMode) return;
    if (toneRef.current && toneRef.current.isAlive) {
      toneRef.current.fadeOut();
      const ref = toneRef.current;
      setTimeout(() => ref.destroy(), 200);
      toneRef.current = null;
    }
    setLiveFreq(null); setActiveChakra(null); lastFreqRef.current = 0;
  }, [healMode]);

  const toggleSound = useCallback(() => {
    if (soundOn) {
      if (toneRef.current && toneRef.current.isAlive) {
        toneRef.current.fadeOut();
        const ref = toneRef.current;
        setTimeout(() => ref.destroy(), 200);
        toneRef.current = null;
      }
      setActiveChakra(null); setLiveFreq(null); setHealMode(false); stopAll();
    }
    setSoundOn(!soundOn);
  }, [soundOn]);

  const toggleHeal = useCallback(() => {
    if (!soundOn) { setSoundOn(true); setHealMode(true); return; }
    if (healMode) {
      setHealMode(false);
      if (toneRef.current && toneRef.current.isAlive) {
        toneRef.current.fadeOut();
        const ref = toneRef.current;
        setTimeout(() => ref.destroy(), 200);
        toneRef.current = null;
      }
      setActiveChakra(null); setLiveFreq(null);
    } else {
      setHealMode(true);
    }
  }, [soundOn, healMode]);

  useEffect(() => {
    return () => {
      if (toneRef.current && toneRef.current.isAlive) { toneRef.current.destroy(); }
    };
  }, []);

  // SOUNDdD hyperbeam lifecycle — start on select, stop on deselect
  useEffect(() => {
    if (formation === "SOUNDdD") {
      setSoundOn(true);
      if (hyperbeamRef.current) { try { hyperbeamRef.current.stop(); } catch {} }
      hyperbeamRef.current = playHyperbeam({ seconds: 600, gain: 0.3, carrierHz: 40000, modulatorHz: 528 });
    } else if (hyperbeamRef.current) {
      try { hyperbeamRef.current.stop(); } catch {}
      hyperbeamRef.current = null;
    }
    return () => {
      if (hyperbeamRef.current) { try { hyperbeamRef.current.stop(); } catch {} hyperbeamRef.current = null; }
    };
  }, [formation]);

  // EB audio frequency lifecycle — play only when touching particles
  useEffect(() => {
    if (formation === "EB") {
      const audio = new Audio(EB_AUDIO_URL);
      audio.loop = true;
      audio.volume = 0.6;
      audio.preload = "auto";
      ebAudioRef.current = audio;
    } else if (ebAudioRef.current) {
      ebAudioRef.current.pause();
      ebAudioRef.current.currentTime = 0;
      ebAudioRef.current = null;
    }
    return () => {
      if (ebAudioRef.current) { ebAudioRef.current.pause(); ebAudioRef.current = null; }
    };
  }, [formation]);

  const computeTargets = useCallback((t) => {
    const particles = particlesRef.current;
    const N = particles.length;
    const audio = audioDataRef.current;
    const audioChaos = soundOn ? audio.energy * 0.4 : 0;
    const audioScale = soundOn ? 1 + audio.bass * 0.3 : 1;

    for (let i = 0; i < N; i++) {
      const ratio = i / N;
      const p = particles[i];
      let tx = 0, ty = 0;

      switch (formation) {
        case "96": {
          const pts96 = get96Points(N);
          const pt = pts96[i];
          const scale96 = audioScale;
          tx = pt.x * scale96;
          ty = pt.y * scale96;
          // Subtle breathing + drift
          const breathe = Math.sin(t * 0.6) * 0.01;
          tx += Math.sin(i * 0.5 + t * 0.4) * breathe;
          ty += Math.cos(i * 0.7 + t * 0.3) * breathe;
          break;
        }
        case "sphere": {
          const phi = Math.acos(1 - 2 * ratio);
          const theta = Math.PI * (1 + Math.sqrt(5)) * i + t * 0.3;
          tx = Math.sin(phi) * Math.cos(theta) * 0.38 * audioScale;
          ty = Math.sin(phi) * Math.sin(theta) * 0.38 * audioScale;
          break;
        }
        case "spiral": {
          const angle = ratio * Math.PI * 12 + t * 0.5;
          const r = ratio * 0.42 * audioScale;
          tx = Math.cos(angle) * r; ty = Math.sin(angle) * r;
          break;
        }
        case "wave": {
          const waveAmp = soundOn ? 0.2 + audio.mid * 0.3 : 0.2;
          tx = (ratio - 0.5) * 0.9;
          ty = Math.sin(ratio * Math.PI * 6 + t) * waveAmp + Math.cos(ratio * Math.PI * 3 + t * 0.7) * 0.1;
          break;
        }
        case "dna": {
          const strand = i % 2;
          const pos = ratio * Math.PI * 8 + t * 0.4;
          tx = Math.cos(pos + strand * Math.PI) * 0.15 * audioScale;
          ty = (ratio - 0.5) * 0.8;
          break;
        }
        case "vortex": {
          const a = ratio * Math.PI * 20 + t * (0.3 + ratio * 0.5);
          const r2 = (0.05 + ratio * 0.38) * audioScale;
          tx = Math.cos(a) * r2; ty = Math.sin(a) * r2;
          break;
        }
        case "grid": {
          const cols = Math.ceil(Math.sqrt(N));
          const col = i % cols; const row = Math.floor(i / cols);
          tx = ((col / cols) - 0.5) * 0.85; ty = ((row / cols) - 0.5) * 0.85;
          const gp = soundOn ? audio.high * 0.04 : 0.015;
          tx += Math.sin(t + col * 0.3) * gp; ty += Math.cos(t + row * 0.3) * gp;
          break;
        }
        case "tetragrammaton": {
          // Sacred geometry pentagram — 5-pointed star
          const starR = 0.38 * audioScale;
          const innerR = starR * 0.382;
          const verts = [];
          for (let v = 0; v < 10; v++) {
            const angle = (v * Math.PI / 5) - Math.PI / 2;
            const rv = v % 2 === 0 ? starR : innerR;
            verts.push({ x: Math.cos(angle) * rv, y: Math.sin(angle) * rv });
          }
          const segLen = N / 10;
          const seg = Math.floor(i / segLen);
          const segT = (i % segLen) / segLen;
          const v0 = verts[seg % 10];
          const v1 = verts[(seg + 1) % 10];
          let stx = v0.x + (v1.x - v0.x) * segT;
          let sty = v0.y + (v1.y - v0.y) * segT;
          const rot = t * 0.15;
          tx = stx * Math.cos(rot) - sty * Math.sin(rot);
          ty = stx * Math.sin(rot) + sty * Math.cos(rot);
          break;
        }
        case "merkabah": {
          // Star tetrahedron — two interlocking equilateral triangles, 3D rotation
          const mr = 0.38 * audioScale;
          const tri1 = [{ x: 0, y: -mr }, { x: mr * 0.866, y: mr * 0.5 }, { x: -mr * 0.866, y: mr * 0.5 }];
          const tri2 = [{ x: 0, y: mr }, { x: mr * 0.866, y: -mr * 0.5 }, { x: -mr * 0.866, y: -mr * 0.5 }];
          const mEdges = [
            [tri1[0], tri1[1]], [tri1[1], tri1[2]], [tri1[2], tri1[0]],
            [tri2[0], tri2[1]], [tri2[1], tri2[2]], [tri2[2], tri2[0]],
          ];
          const mSegLen = N / mEdges.length;
          const mSeg = Math.floor(i / mSegLen);
          const mSegT = (i % mSegLen) / mSegLen;
          const me = mEdges[Math.min(mSeg, mEdges.length - 1)];
          let mx3 = me[0].x + (me[1].x - me[0].x) * mSegT;
          let my3 = me[0].y + (me[1].y - me[0].y) * mSegT;
          const rotY = t * 0.25;
          const zd = mx3 * Math.sin(rotY);
          const persp = 1 / (1 + zd * 0.5);
          tx = mx3 * Math.cos(rotY) * persp;
          ty = my3 * persp;
          break;
        }
        case "13": {
          const pts13 = get13Points(N);
          const pt13 = pts13[i];
          tx = pt13.x * audioScale;
          ty = pt13.y * audioScale;
          const shimmer = Math.sin(i * 0.8 + t * 2) * 0.008;
          tx += shimmer;
          ty += Math.cos(i * 1.1 + t * 1.5) * 0.005;
          break;
        }
        case "SOUNDdD": {
          const ptsSd = getSOUNDdDPoints(N);
          const ptSd = ptsSd[i];
          tx = ptSd.x * audioScale;
          ty = ptSd.y * audioScale;
          const pulse = Math.sin(t * 3 + ratio * Math.PI * 4) * 0.01;
          tx += pulse;
          ty += Math.sin(t * 2 + i * 0.01) * 0.005;
          break;
        }
        case "EB": {
          const ptsEB = getEBPoints(N);
          const ptEB = ptsEB[i];
          tx = ptEB.x * audioScale;
          ty = ptEB.y * audioScale;
          // Warm floating drift — Knicks energy
          const drift = Math.sin(i * 0.4 + t * 0.8) * 0.012;
          tx += drift;
          ty += Math.cos(i * 0.6 + t * 0.6) * 0.008;
          break;
        }
        default: break;
      }

      const totalChaos = chaos + audioChaos;
      tx += Math.sin(i * 1.7 + t * 2) * totalChaos * 0.08;
      ty += Math.cos(i * 2.3 + t * 1.5) * totalChaos * 0.08;
      p.tx = tx; p.ty = ty; p.hue = ratio;
      p.size = 1 + Math.sin(ratio * Math.PI * 4 + t) * 0.5 + (soundOn ? audio.bass * 1.8 : 0);
    }
  }, [formation, chaos, soundOn]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const animate = (timestamp) => {
      const delta = lastFrameRef.current ? Math.min((timestamp - lastFrameRef.current) / 1000, 0.05) : 0.016;
      lastFrameRef.current = timestamp;
      const audioSpeed = soundOn ? 1 + audioDataRef.current.energy * 2 : 1;
      if (!paused) timeRef.current += delta * speed * audioSpeed;
      const t = timeRef.current;
      const w = canvas.width; const h = canvas.height;
      const cx = w / 2; const cy = h / 2;
      const scale = Math.min(w, h);

      ctx.fillStyle = soundOn && audioDataRef.current.energy > 0.1
        ? `rgba(0,0,0,${0.06 + audioDataRef.current.energy * 0.12})`
        : "rgba(0,0,0,0.12)";
      ctx.fillRect(0, 0, w, h);

      // EB background — silhouette with Knicks glow
      if (formation === "EB" && ebBgImgRef.current) {
        // Full black clear first to prevent trail accumulation over BG
        ctx.fillStyle = "rgba(0,0,0,0.92)";
        ctx.fillRect(0, 0, w, h);
        ctx.save();
        ctx.globalAlpha = 0.55 + Math.sin(t * 0.4) * 0.08;
        const imgW = ebBgImgRef.current.width;
        const imgH = ebBgImgRef.current.height;
        const fitScale = Math.max(w / imgW, h / imgH);
        const dw = imgW * fitScale;
        const dh = imgH * fitScale;
        ctx.drawImage(ebBgImgRef.current, (w - dw) / 2, (h - dh) / 2, dw, dh);
        ctx.globalAlpha = 1;
        ctx.restore();
      }

      computeTargets(t);

      const particles = particlesRef.current;
      const mx = mouseRef.current.x; const my = mouseRef.current.y;
      const mActive = mouseRef.current.active;
      const mDown = mouseRef.current.down;
      const audio = audioDataRef.current;
      const activeHue = activeChakra !== null ? CHAKRAS[activeChakra].hue : null;

      // Center glow for "96" formation
      if (formation === "96") {
        const glowR = 30 + (soundOn ? audio.energy * 60 : Math.sin(t * 1.2) * 10);
        const glowGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
        const glowHue = activeHue !== null ? activeHue : 0;
        glowGrad.addColorStop(0, `hsla(${glowHue}, 100%, 80%, ${soundOn ? 0.15 + audio.energy * 0.3 : 0.06})`);
        glowGrad.addColorStop(1, `hsla(${glowHue}, 80%, 40%, 0)`);
        ctx.beginPath(); ctx.arc(cx, cy, glowR, 0, Math.PI * 2);
        ctx.fillStyle = glowGrad; ctx.fill();
      }

      // Center glow for "EB" — Knicks orange/blue
      if (formation === "EB") {
        const ebGlowR = 35 + Math.sin(t * 1.5) * 12;
        const ebGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, ebGlowR);
        const ebCycle = Math.sin(t * 0.8) * 0.5 + 0.5;
        const ebOrange = `rgba(245,132,38,${0.08 + ebCycle * 0.06})`;
        const ebBlue = `rgba(0,107,182,${0.06 + (1 - ebCycle) * 0.04})`;
        ebGrad.addColorStop(0, ebOrange);
        ebGrad.addColorStop(0.6, ebBlue);
        ebGrad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath(); ctx.arc(cx, cy, ebGlowR, 0, Math.PI * 2);
        ctx.fillStyle = ebGrad; ctx.fill();
      }

      // GLYPH palette color mapping
      const GLYPH_PALETTES = {
        rainbow: [0, 60, 120, 180, 240, 300],
        fire: [0, 15, 30, 45, 20, 10],
        blizzard: [190, 200, 210, 220, 200, 195],
        neon: [180, 280, 320, 160, 300, 200],
        sunset: [15, 30, 280, 310, 340, 20],
        matrix: [120, 130, 115, 125, 135, 110],
        vapor: [280, 300, 180, 200, 310, 190],
        ocean: [200, 210, 190, 220, 180, 205],
        toxic: [80, 100, 120, 75, 90, 110],
        plasma: [280, 310, 340, 20, 290, 330],
        gold: [40, 45, 35, 50, 38, 42],
        lava: [0, 10, 20, 30, 5, 15],
        aurora: [140, 160, 260, 280, 150, 270],
        candy: [330, 340, 350, 30, 20, 10],
        copper: [20, 25, 15, 30, 18, 22],
        electric: [190, 50, 180, 60, 200, 40],
        forest: [100, 120, 140, 90, 110, 130],
        galaxy: [260, 280, 320, 340, 270, 310],
        infrared: [0, 350, 340, 355, 5, 345],
        miami: [180, 330, 200, 310, 190, 320],
        rust: [15, 20, 10, 25, 12, 18],
        synthwave: [280, 300, 260, 320, 270, 290],
        hologram: [180, 200, 280, 300, 190, 290],
      };

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const targetX = cx + p.tx * scale;
        const targetY = cy + p.ty * scale;

        let fx = 0, fy = 0;
        if (mActive) {
          const mxPx = mx * w; const myPx = my * h;
          const dx = p.x - mxPx; const dy = p.y - myPx;
          const dist = Math.sqrt(dx * dx + dy * dy) + 1;
          const baseForce = mDown && soundOn ? 15000 + audio.bass * 25000 : 8000;
          const force = Math.min(baseForce / (dist * dist), mDown ? 20 : 12);
          fx = (dx / dist) * force; fy = (dy / dist) * force;
        }

        const springK = soundOn ? 0.04 + audio.mid * 0.04 : 0.04;
        p.vx = (p.vx + (targetX - p.x) * springK + fx) * (soundOn ? 0.88 - audio.high * 0.08 : 0.88);
        p.vy = (p.vy + (targetY - p.y) * springK + fy) * (soundOn ? 0.88 - audio.high * 0.08 : 0.88);
        p.x += p.vx; p.y += p.vy;

        // GLYPH motion effects
        if (glyphMotion) {
          const ratio = i / particles.length;
          const gmx = p.x; const gmy = p.y;
          if (glyphMotion === "wave") { p.y += Math.sin(gmx * 0.02 + t * 3) * 3; }
          else if (glyphMotion === "ripple") { const rd = Math.sqrt((gmx - cx) * (gmx - cx) + (gmy - cy) * (gmy - cy)); p.x += Math.cos(rd * 0.05 - t * 4) * 2; p.y += Math.sin(rd * 0.05 - t * 4) * 2; }
          else if (glyphMotion === "wind") { p.x += Math.sin(t * 2 + ratio * 10) * 4; }
          else if (glyphMotion === "sparkle") { if (Math.random() < 0.03) { p.vx += (Math.random() - 0.5) * 8; p.vy += (Math.random() - 0.5) * 8; } }
          else if (glyphMotion === "pulse") { const ps = Math.sin(t * 2) * 0.15; p.x += (p.x - cx) * ps * 0.02; p.y += (p.y - cy) * ps * 0.02; }
          else if (glyphMotion === "rain") { p.y += 1 + Math.random() * 2; if (p.y > h) p.y = 0; }
          else if (glyphMotion === "glitch") { if (Math.random() < 0.01) { p.x += (Math.random() - 0.5) * 40; p.y += (Math.random() - 0.5) * 20; } }
          else if (glyphMotion === "breathe") { const br = Math.sin(t * 0.8) * 0.08; p.x += (p.x - cx) * br * 0.015; p.y += (p.y - cy) * br * 0.015; }
        }

        let hue;
        if (soundOn && activeHue !== null) {
          const nh = chakraColorAt(p.hue);
          hue = nh + (activeHue - nh) * (0.4 + audio.energy * 0.4);
        } else {
          hue = chakraColorAt(p.hue);
        }

        // GLYPH palette override
        if (glyphPalette && GLYPH_PALETTES[glyphPalette]) {
          const pal = GLYPH_PALETTES[glyphPalette];
          const pidx = (i + Math.floor(t * 30)) % pal.length;
          const nextIdx = (pidx + 1) % pal.length;
          const blend = ((i + t * 30) % 1);
          hue = pal[pidx] + (pal[nextIdx] - pal[pidx]) * blend;
        }

        const vel = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        let sat, light, alpha;
        if (formation === "13") {
          // Liquid metal: chrome/silver with shimmer
          hue = 210 + Math.sin(i * 0.3 + t * 2) * 15;
          sat = 8 + Math.sin(i * 0.7 + t * 3) * 6;
          light = 65 + vel * 3 + Math.sin(i * 0.5 + t * 4) * 12;
          alpha = 0.7 + vel * 0.03;
        } else if (formation === "SOUNDdD") {
          // Hyperbeam: bright red-white pulsing
          const beamPulse = Math.sin(t * 6 + i * 0.02) * 0.5 + 0.5;
          hue = 0 + beamPulse * 20;
          sat = 90 - beamPulse * 40;
          light = 50 + vel * 3 + beamPulse * 20;
          alpha = 0.6 + beamPulse * 0.3;
        } else if (formation === "EB") {
          // Knicks colors: blue #006BB6 (hue ~207) and orange #F58426 (hue ~28)
          const knicksCycle = Math.sin(i * 0.3 + t * 1.5) * 0.5 + 0.5;
          hue = knicksCycle < 0.5 ? 207 + knicksCycle * 30 : 28 + (1 - knicksCycle) * 15;
          sat = 85 + Math.sin(i * 0.5 + t * 2) * 10;
          light = 50 + vel * 3 + Math.sin(i * 0.8 + t * 3) * 8;
          alpha = 0.75 + vel * 0.03;
        } else {
          sat = soundOn ? 85 + audio.energy * 15 : 80;
          light = Math.min(40 + vel * 3 + (soundOn ? audio.energy * 15 : 0), 70);
          alpha = Math.min(0.55 + vel * 0.04 + (soundOn ? audio.energy * 0.3 : 0), 1);
        }

        ctx.fillStyle = `hsla(${hue},${sat}%,${light}%,${alpha})`;
        // GLYPH charset overlay — render characters instead of dots
        const GLYPH_CHARSETS = {
          ascii: "@#%&*+=-:. ", braille: "⠁⠃⠇⠏⠟⠿⡿⣿ ", katakana: "ァアィイゥウェエォオカキクケコ",
          math: "∑∏∫∂√∞≈≠≤≥±×÷", morse: "·—", dna: "ACGT",
          geometric: "◆◇○●□■△▽◈◉", blocks: "█▓▒░", minimal: ".:+*#",
          etching: "/\\|—_(){}[]",
        };
        if (glyphCharset && GLYPH_CHARSETS[glyphCharset]) {
          const chars = GLYPH_CHARSETS[glyphCharset];
          const ch = chars[i % chars.length];
          ctx.font = `${Math.max(p.size * 3, 6)}px monospace`;
          ctx.fillText(ch, p.x - p.size, p.y + p.size * 0.5);
        } else if (p.size < 1.5) {
          ctx.fillRect(p.x - p.size, p.y - p.size, p.size * 2, p.size * 2);
        } else {
          ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
        }

        if (vel > 5) {
          ctx.beginPath(); ctx.arc(p.x, p.y, p.size * 2, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${hue},100%,50%,${Math.min(vel * 0.01, 0.12)})`;
          ctx.fill();
        }
      }

      // Chakra spectrum bar
      if (soundOn) {
        const barH = 4; const barY = h - barH; const segW = w / 7;
        for (let c = 0; c < 7; c++) {
          ctx.fillStyle = activeChakra === c ? CHAKRAS[c].color : CHAKRAS[c].color + "30";
          ctx.fillRect(c * segW, barY, segW - 1, barH);
        }
      }

      // Cursor ring
      if (soundOn && mDown && mActive) {
        const mxPx = mx * w; const myPx = my * h;
        const idx = chakraIndexAtFreq(freqAtRatio(mx));
        ctx.beginPath(); ctx.arc(mxPx, myPx, 20 + audio.energy * 30, 0, Math.PI * 2);
        ctx.strokeStyle = CHAKRAS[idx].color + "80"; ctx.lineWidth = 2; ctx.stroke();
        ctx.font = "10px monospace"; ctx.fillStyle = CHAKRAS[idx].color;
        ctx.fillText(`${Math.round(freqAtRatio(mx))} Hz`, mxPx + 25, myPx - 10);
        ctx.fillText(CHAKRAS[idx].name, mxPx + 25, myPx + 2);
      }

      // UV ambient overlay
      if (soundOn && audio.energy > 0.03) {
        const uvAlpha = audio.energy * 0.12;
        const uvHue = 270 + audio.freqPeak * 30;
        const uvGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, scale * 0.5);
        uvGrad.addColorStop(0, `hsla(${uvHue}, 80%, 40%, ${uvAlpha})`);
        uvGrad.addColorStop(1, `hsla(${uvHue}, 60%, 20%, 0)`);
        ctx.fillStyle = uvGrad; ctx.fillRect(0, 0, w, h);
      }

      animRef.current = requestAnimationFrame(animate);
    };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * (window.devicePixelRatio > 1 ? 1.5 : 1);
      canvas.height = 420 * (window.devicePixelRatio > 1 ? 1.5 : 1);
      for (let i = 0; i < particlesRef.current.length; i++) {
        if (!particlesRef.current[i].x) {
          particlesRef.current[i].x = canvas.width / 2 + (Math.random() - 0.5) * 100;
          particlesRef.current[i].y = canvas.height / 2 + (Math.random() - 0.5) * 100;
        }
      }
    };
    resize();
    window.addEventListener("resize", resize);
    animRef.current = requestAnimationFrame(animate);
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener("resize", resize); };
  }, [speed, formation, chaos, paused, computeTargets, soundOn, activeChakra]);

  const hapticThrottleRef = useRef(0);

  const handlePointer = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect(); if (!rect) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    mouseRef.current.x = (clientX - rect.left) / rect.width;
    mouseRef.current.y = (clientY - rect.top) / rect.height;
    mouseRef.current.active = true;
    updateMouseTone(mouseRef.current.x);
    // Throttled haptic during drag — every 120ms
    if (mouseRef.current.down) {
      const now = Date.now();
      if (now - hapticThrottleRef.current > 120) {
        hapticThrottleRef.current = now;
        const hz = freqAtRatio(mouseRef.current.x);
        hapticForFreq(hz);
      }
    }
  };
  const handleDown = (e) => {
    if (!isAudioUnlocked()) unlockAudio();
    mouseRef.current.down = true; haptic(25); handlePointer(e);
    if (formation === "EB" && ebAudioRef.current) {
      ebAudioRef.current.play().catch(() => {});
      haptic(40);
    }
  };
  const handleUp = () => {
    mouseRef.current.down = false; stopMouseTone();
    if (formation === "EB" && ebAudioRef.current) {
      ebAudioRef.current.pause();
    }
  };
  const handleLeave = () => {
    mouseRef.current.active = false; mouseRef.current.down = false; stopMouseTone();
    if (formation === "EB" && ebAudioRef.current) {
      ebAudioRef.current.pause();
    }
  };

  return (
    <Card className="sg-frame border-[#FF0000]/20" data-testid="particle-lab">
      <CardHeader className="pb-2">
        <CardTitle className="text-[#FF0000] text-sm tracking-widest cursor-pointer select-none" data-testid="particle-lab-title"
          onClick={() => {
            const now = Date.now();
            if (now - glyphTapRef.current < 400) { setGlyphOpen(v => !v); hapticFormation(); }
            glyphTapRef.current = now;
          }}>
          &gt; PARTICLE_LAB {glyphOpen && <span className="text-[8px] text-[#39FF14]/50 ml-2 tracking-wider">GLYPH</span>}
        </CardTitle>
        <p className="text-[10px] tracking-widest uppercase text-[#666]">
          {healMode ? "SOUND MODE — Cycling frequency spectrum" : formation === "EB" ? "Touch E.B. particles to play audio" : soundOn ? "Click & drag particles to play frequencies" : "Digital fidget — Touch to interact · 1396 particles"}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Formation + controls */}
        <div className="flex flex-wrap gap-1.5" data-testid="particle-formations">
          {FORMATIONS.map((f) => {
            const isHealBtn = f === "HEAL";
            const isActive = isHealBtn ? healMode : formation === f;
            return (
              <button key={f} onClick={() => {
                if (isHealBtn) { toggleHeal(); }
                else { setFormation(f); hapticFormation(); }
              }}
                className={`px-2.5 py-1 text-[9px] tracking-widest uppercase font-mono border transition-colors ${
                  isActive
                    ? isHealBtn
                      ? "border-[#CC33FF]/60 bg-[#CC33FF]/15 text-[#CC33FF] shadow-[0_0_8px_rgba(204,51,255,0.3)]"
                      : f === "EB"
                      ? "border-[#F58426]/60 bg-[#006BB6]/20 text-[#F58426] shadow-[0_0_8px_rgba(245,132,38,0.4)]"
                      : "border-[#FF0000]/60 bg-[#FF0000]/15 text-[#FF0000]"
                    : f === "EB"
                    ? "border-[#006BB6]/30 bg-black text-[#006BB6] hover:text-[#F58426] hover:border-[#F58426]/50"
                    : "border-[#FF0000]/20 bg-black text-[#666] hover:text-[#FF0000] hover:border-[#FF0000]/40"
                }`}
                data-testid={`formation-${f}`}
              >{f}</button>
            );
          })}
          <button onClick={toggleSound}
            className={`px-3 py-1 text-[9px] tracking-widest uppercase font-mono border-2 transition-all ml-auto ${
              soundOn ? "border-[#FF0000] bg-[#FF0000]/20 text-[#FF0000] shadow-[0_0_10px_rgba(255,0,0,0.3)]"
                      : "border-[#FF0000]/30 bg-black text-[#666] hover:text-[#FF0000]"
            }`}
            data-testid="sound-toggle"
          >SOUND {soundOn ? "ON" : "OFF"}</button>
          <button onClick={() => setPaused(!paused)}
            className={`px-2.5 py-1 text-[9px] tracking-widest uppercase font-mono border transition-colors ${
              paused ? "border-[#FF0000]/60 bg-[#FF0000]/15 text-[#FF0000]" : "border-[#FF0000]/20 bg-black text-[#666] hover:text-[#FF0000]"
            }`}
            data-testid="particle-pause"
          >{paused ? "Resume" : "Pause"}</button>
        </div>

        {/* ─── GLYPH SECRET MENU ─── */}
        {glyphOpen && (
          <div className="border border-[#39FF14]/15 bg-[#001a00]/60 p-3 space-y-3" data-testid="glyph-panel">
            <div className="flex items-center justify-between">
              <div className="text-[9px] font-mono tracking-[0.2em] text-[#39FF14]/50 uppercase">GLYPH Engine</div>
              <button onClick={() => { setGlyphPalette(null); setGlyphMotion(null); setGlyphCharset(null); }}
                className="text-[7px] font-mono text-[#39FF14]/25 hover:text-[#39FF14]/50 border border-[#39FF14]/10 px-1.5 py-0.5" data-testid="glyph-reset">RESET</button>
            </div>

            {/* Palettes */}
            <div className="space-y-1">
              <div className="text-[8px] font-mono tracking-wider text-[#39FF14]/30">PALETTE</div>
              <div className="flex flex-wrap gap-1" data-testid="glyph-palettes">
                {[
                  { id: "rainbow", c1: "#f00", c2: "#0f0" }, { id: "fire", c1: "#f80", c2: "#f00" },
                  { id: "blizzard", c1: "#8cf", c2: "#fff" }, { id: "neon", c1: "#0ff", c2: "#f0f" },
                  { id: "sunset", c1: "#f60", c2: "#c0f" }, { id: "matrix", c1: "#0f0", c2: "#030" },
                  { id: "vapor", c1: "#f0f", c2: "#0ff" }, { id: "ocean", c1: "#06f", c2: "#0ff" },
                  { id: "toxic", c1: "#cf0", c2: "#0a0" }, { id: "plasma", c1: "#f0f", c2: "#f80" },
                  { id: "gold", c1: "#fa0", c2: "#a60" }, { id: "lava", c1: "#f00", c2: "#f80" },
                  { id: "aurora", c1: "#0f8", c2: "#80f" }, { id: "candy", c1: "#f6a", c2: "#fa6" },
                  { id: "copper", c1: "#c84", c2: "#a62" }, { id: "electric", c1: "#0af", c2: "#ff0" },
                  { id: "forest", c1: "#080", c2: "#4a0" }, { id: "galaxy", c1: "#40f", c2: "#f08" },
                  { id: "infrared", c1: "#f00", c2: "#800" }, { id: "miami", c1: "#0ff", c2: "#f0a" },
                  { id: "rust", c1: "#a40", c2: "#620" }, { id: "synthwave", c1: "#f0f", c2: "#40f" },
                  { id: "hologram", c1: "#0ff", c2: "#f0f" },
                ].map(p => (
                  <button key={p.id} onClick={() => setGlyphPalette(glyphPalette === p.id ? null : p.id)}
                    className={`w-6 h-6 border transition-all ${glyphPalette === p.id ? "border-[#39FF14] shadow-[0_0_6px_rgba(57,255,20,0.3)] scale-110" : "border-[#333]/50 hover:border-[#39FF14]/40"}`}
                    style={{ background: `linear-gradient(135deg, ${p.c1}, ${p.c2})` }}
                    title={p.id} data-testid={`glyph-pal-${p.id}`} />
                ))}
              </div>
            </div>

            {/* Motion */}
            <div className="space-y-1">
              <div className="text-[8px] font-mono tracking-wider text-[#39FF14]/30">MOTION</div>
              <div className="flex flex-wrap gap-1" data-testid="glyph-motions">
                {["wave","ripple","wind","sparkle","pulse","rain","glitch","breathe"].map(m => (
                  <button key={m} onClick={() => setGlyphMotion(glyphMotion === m ? null : m)}
                    className={`px-2 py-1 text-[8px] font-mono tracking-wider border transition-all ${
                      glyphMotion === m ? "border-[#39FF14]/50 bg-[#39FF14]/10 text-[#39FF14]" : "border-[#333]/50 text-[#555] hover:text-[#39FF14]/60"}`}
                    data-testid={`glyph-motion-${m}`}>{m}</button>
                ))}
              </div>
            </div>

            {/* Charset overlay */}
            <div className="space-y-1">
              <div className="text-[8px] font-mono tracking-wider text-[#39FF14]/30">CHARSET</div>
              <div className="flex flex-wrap gap-1" data-testid="glyph-charsets">
                {[
                  { id: "ascii", label: "ASCII", chars: "@#%&*+=-:. " },
                  { id: "braille", label: "Braille", chars: "⠁⠃⠇⠏⠟⠿⡿⣿ " },
                  { id: "katakana", label: "カタカナ", chars: "ァアィイゥウェエォオカキクケコ" },
                  { id: "math", label: "Math", chars: "∑∏∫∂√∞≈≠≤≥±×÷" },
                  { id: "morse", label: "Morse", chars: "·—·—·— " },
                  { id: "dna", label: "DNA", chars: "ACGT " },
                  { id: "geometric", label: "Geo", chars: "◆◇○●□■△▽◈◉" },
                  { id: "blocks", label: "Blocks", chars: "█▓▒░ " },
                  { id: "minimal", label: "Min", chars: ".:+*# " },
                  { id: "etching", label: "Etch", chars: "/\\|—_(){}[]" },
                ].map(c => (
                  <button key={c.id} onClick={() => setGlyphCharset(glyphCharset === c.id ? null : c.id)}
                    className={`px-2 py-1 text-[8px] font-mono tracking-wider border transition-all ${
                      glyphCharset === c.id ? "border-[#39FF14]/50 bg-[#39FF14]/10 text-[#39FF14]" : "border-[#333]/50 text-[#555] hover:text-[#39FF14]/60"}`}
                    data-testid={`glyph-charset-${c.id}`}>{c.label}</button>
                ))}
              </div>
            </div>

            <div className="text-[7px] font-mono text-[#39FF14]/15 text-center tracking-widest">GLYPH ENGINE · ASTERIA R&T · DOUBLE-TAP TITLE TO TOGGLE</div>
          </div>
        )}

        {/* Heal mode chakra indicator */}
        {healMode && activeChakra !== null && (
          <div className="flex items-center gap-2" data-testid="heal-indicator">
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: CHAKRAS[activeChakra].color }} />
            <span className="text-[10px] font-mono tracking-widest" style={{ color: CHAKRAS[activeChakra].color }}>
              {CHAKRAS[activeChakra].name} — {CHAKRAS[activeChakra].hz} Hz
            </span>
            <div className="flex-1 h-0.5 bg-black border border-[#FF0000]/10 overflow-hidden">
              <div className="h-full transition-all duration-1000" style={{
                width: `${((activeChakra + 1) / 7) * 100}%`,
                background: `linear-gradient(90deg, ${CHAKRAS[0].color}, ${CHAKRAS[activeChakra].color})`,
              }} />
            </div>
          </div>
        )}

        {/* Canvas */}
        <div className="relative border border-[#FF0000]/15 bg-black overflow-hidden" style={{ height: "420px" }}>
          <canvas ref={canvasRef} className="w-full h-full"
            style={{ display: "block", cursor: soundOn ? "crosshair" : "default" }}
            onMouseMove={handlePointer} onMouseDown={handleDown} onMouseUp={handleUp}
            onMouseEnter={() => { mouseRef.current.active = true; }}
            onMouseLeave={handleLeave}
            onTouchStart={handleDown} onTouchMove={handlePointer} onTouchEnd={handleLeave}
            data-testid="particle-canvas"
          />
          <div className="absolute top-2 left-3 text-[9px] font-mono text-[#FF0000]/50 pointer-events-none">
            {PARTICLE_COUNT} PARTICLES — {formation.toUpperCase()}
            {soundOn && liveFreq && ` — ${liveFreq} Hz`}
          </div>
          <div className="absolute top-2 right-3 text-[9px] font-mono pointer-events-none">
            <span className={healMode ? "text-[#CC33FF]" : soundOn ? "text-[#FF0000]" : "text-[#FF0000]/50"}>
              {paused ? "PAUSED" : healMode ? "HEALING" : soundOn ? "SOUND REACTIVE" : "LIVE"}
            </span>
          </div>

          {/* HEAL overlay — APPLY HEALING FREQUENCIES inside the canvas */}
          {healMode && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent pointer-events-auto" style={{ maxHeight: "70%" }} data-testid="heal-overlay">
              <div className="p-3 sm:p-4 space-y-3 overflow-y-auto" style={{ maxHeight: "280px" }}>

                {/* Applied frequency banner */}
                {appliedFreq && (
                  <div className="flex items-center gap-2 bg-[#FF0000]/10 border border-[#FF0000]/30 rounded px-3 py-2" data-testid="applied-freq-banner">
                    <div className="w-2 h-2 rounded-full bg-[#FF0000] animate-pulse" />
                    <span className="text-xs font-mono text-[#FF0000] flex-1 tracking-wider">{appliedFreq.frequency} Hz — {appliedFreq.name}</span>
                    <button onClick={stopApplied} className="text-[9px] font-mono text-[#FF0000]/60 hover:text-[#FF0000] px-1.5 py-0.5 border border-[#FF0000]/30 rounded" data-testid="stop-applied-btn">STOP</button>
                  </div>
                )}

                {/* Header + Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-mono font-bold text-[#FF0000] tracking-wider">APPLY HEALING FREQUENCIES</div>
                    <div className="text-[9px] font-mono text-[#666] mt-0.5">Tap a frequency to apply it to the Particle Lab</div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => setShowPresets(!showPresets)}
                      className={`text-[9px] font-mono h-6 px-2 ${showPresets ? "bg-[#FF0000] text-black" : "bg-transparent border border-[#FF0000]/40 text-[#FF0000]"}`}
                      data-testid="toggle-presets-btn"
                    >
                      {showPresets ? "HIDE" : "PRESETS"}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => onBrowseArchive ? onBrowseArchive() : navigate("/library")}
                      className="text-[9px] font-mono h-6 px-2 bg-transparent border border-[#FF0000]/40 text-[#FF0000] hover:bg-[#FF0000]/10"
                      data-testid="heal-browse-library-btn"
                    >
                      ARCHIVE
                    </Button>
                  </div>
                </div>

                {/* Quick-apply row — always visible */}
                <div className="flex gap-1.5 flex-wrap" data-testid="heal-quick-freqs">
                  {[432, 528, 396, 963, 174, 741].map(hz => {
                    const isActive = appliedFreq?.frequency === hz;
                    return (
                      <button
                        key={hz}
                        onClick={() => isActive ? stopApplied() : applyFrequency(hz, `${hz} Hz`, "solfeggio")}
                        className={`px-2.5 py-1.5 text-[10px] font-mono tracking-wider rounded border transition-all ${
                          isActive
                            ? "bg-[#FF0000] text-black border-[#FF0000] shadow-[0_0_10px_rgba(255,0,0,0.4)]"
                            : "border-[#FF0000]/30 text-[#FF0000]/70 hover:border-[#FF0000] hover:text-[#FF0000] hover:bg-[#FF0000]/5"
                        }`}
                        data-testid={`quick-freq-${hz}`}
                      >
                        {hz}Hz
                      </button>
                    );
                  })}
                </div>

                {/* Full presets grid */}
                {showPresets && presets && (
                  <div className="space-y-2" data-testid="presets-panel">
                    <div className="text-[8px] font-mono text-[#666] tracking-[0.2em] uppercase">Solfeggio</div>
                    <div className="grid grid-cols-2 gap-1">
                      {presets.solfeggio?.map(p => {
                        const isActive = appliedFreq?.frequency === p.frequency;
                        return (
                          <button
                            key={p.frequency}
                            onClick={() => isActive ? stopApplied() : applyFrequency(p.frequency, p.name, p.source)}
                            className={`flex items-center gap-2 px-2 py-1.5 text-left rounded border transition-all ${
                              isActive
                                ? "bg-[#FF0000]/15 border-[#FF0000]/50 text-[#FF0000]"
                                : "border-[#222] text-[#888] hover:border-[#FF0000]/30 hover:text-[#FF0000]"
                            }`}
                            data-testid={`preset-${p.frequency}`}
                          >
                            <span className="text-xs font-mono font-bold w-10 shrink-0">{p.frequency}</span>
                            <span className="text-[8px] font-mono truncate">{p.name.replace(`${p.frequency} Hz — `, "")}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-[8px] font-mono text-[#666] tracking-[0.2em] uppercase mt-2">Popular</div>
                    <div className="grid grid-cols-2 gap-1">
                      {presets.popular?.map(p => {
                        const isActive = appliedFreq?.frequency === p.frequency;
                        return (
                          <button
                            key={`pop-${p.frequency}`}
                            onClick={() => isActive ? stopApplied() : applyFrequency(p.frequency, p.name, p.source)}
                            className={`flex items-center gap-2 px-2 py-1.5 text-left rounded border transition-all ${
                              isActive
                                ? "bg-[#FF0000]/15 border-[#FF0000]/50 text-[#FF0000]"
                                : "border-[#222] text-[#888] hover:border-[#FF0000]/30 hover:text-[#FF0000]"
                            }`}
                            data-testid={`preset-pop-${p.frequency}`}
                          >
                            <span className="text-xs font-mono font-bold w-10 shrink-0">{p.frequency}</span>
                            <span className="text-[8px] font-mono truncate">{p.name.replace(`${p.frequency} Hz — `, "")}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {soundOn && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-1 pointer-events-none">
              {CHAKRAS.map((c, i) => (
                <div key={c.name} className="flex items-center gap-1" style={{ opacity: activeChakra === i ? 1 : 0.3 }}>
                  <div className="w-1.5 h-1.5" style={{ backgroundColor: c.color }} />
                  <span className="text-[7px] font-mono" style={{ color: c.color }}>{c.hz}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sliders */}
        <div className="grid grid-cols-2 gap-3" data-testid="particle-controls">
          <div className="space-y-1">
            <label className="text-[9px] font-mono tracking-widest uppercase text-[#FF0000]/60">Speed: {speed.toFixed(1)}x</label>
            <input type="range" min="0" max="3" step="0.1" value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              className="w-full accent-[#FF0000] h-1" data-testid="speed-slider" />
          </div>
          <div className="space-y-1">
            <label className="text-[9px] font-mono tracking-widest uppercase text-[#FF0000]/60">Chaos: {(chaos * 100).toFixed(0)}%</label>
            <input type="range" min="0" max="1" step="0.05" value={chaos}
              onChange={(e) => setChaos(Number(e.target.value))}
              className="w-full accent-[#FF0000] h-1" data-testid="chaos-slider" />
          </div>
        </div>

        <div className="text-[9px] font-mono text-[#666] tracking-wider">
          {healMode
            ? "Auto-cycling all 7 chakra frequencies — Sit back and let the particles heal"
            : soundOn
            ? "Click & drag to play frequencies — Left=Root 396Hz, Right=Crown 963Hz — Haptic feedback on touch"
            : "96 MIRRORS formation · 7 formations · Toggle SOUND ON for frequencies · HEAL for auto-cycle"
          }
        </div>
      </CardContent>
    </Card>
  );
}

export default ParticleLab;
